/**
 * Filling a policy's name and description from the template the author picked, without ever
 * taking back a field they typed in themselves.
 *
 * One rule covers every case: a field is ours to write when it is blank, or when it still holds
 * exactly what the previous pick put there. Everything else - text the author typed, and the name
 * of a policy being EDITED, which arrives pre-filled from the server - belongs to them.
 *
 * Kept as a pure function because the sequence is the thing worth testing: pick a template, type
 * over the name, pick another, and the name has to survive while the description keeps moving.
 */

/** What the last pick wrote into each field, or null for a field no pick has ever written. */
export interface AutofilledValues {
  name: string | null
  description: string | null
}

export const NO_AUTOFILL: AutofilledValues = { name: null, description: null }

/**
 * The value a field should hold after a template pick.
 *
 * `current.trim()` so a field holding only spaces counts as blank. The comparison against
 * `lastAutofilled` is exact and untrimmed: if the author touched the autofilled text at all,
 * even to add a space, they own it.
 */
export function autofillField(current: string, lastAutofilled: string | null, next: string): string {
  if (current.trim() === '') return next
  if (lastAutofilled !== null && current === lastAutofilled) return next
  return current
}

export interface AutofillState {
  name: string
  description: string
  autofilled: AutofilledValues
}

/**
 * Apply a pick to the form's name/description state.
 *
 * A field is remembered as autofilled ONLY if this pick actually wrote it. A field the author owns
 * keeps the previous marker, so it stays theirs on every later pick rather than becoming
 * replaceable again the moment a template happens to match it.
 */
export function applyTemplatePick(
  state: AutofillState,
  template: { label: string; description: string },
): AutofillState {
  const name        = autofillField(state.name, state.autofilled.name, template.label)
  const description = autofillField(state.description, state.autofilled.description, template.description)
  return {
    name,
    description,
    autofilled: {
      name:        name === template.label ? template.label : state.autofilled.name,
      description: description === template.description ? template.description : state.autofilled.description,
    },
  }
}
